import { ArrowRight } from 'lucide-react'
import { LANDING_PAGE_ROUTE, LANDING_PAGE_SECTION, LANDING_PAGE_UI } from '@propbix/constants/public'
import { ButtonLink } from '@/components/Button'
import { CapabilityExplorer } from '@/components/CapabilityExplorer'
import { DayTimeline } from '@/components/DayTimeline'
import { EcosystemMap } from '@/components/EcosystemMap'
import { FaqAccordion } from '@/components/FaqAccordion'
import { FinalCta } from '@/components/FinalCta'
import { OwnerCommandCenter } from '@/components/OwnerCommandCenter'
import { ProblemStrip } from '@/components/ProblemStrip'
import { Reveal } from '@/components/Reveal'
import { SiteFooter } from '@/components/SiteFooter'
import { SiteHeader } from '@/components/SiteHeader'
import { WorkflowExplorer } from '@/components/WorkflowExplorer'
import { tenantAppHref } from '@/lib/tenant-app-url'

export function LandingPage() {
  return (
    <div className="relative min-h-screen bg-background text-ink">
      <SiteHeader />

      <main id="main">
        <section className="hero-surface section-band" aria-labelledby="hero-heading">
          <div className="page-shell relative z-[1] pt-[clamp(1.75rem,4vw,3.5rem)] pb-6 md:pb-10">
            <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,0.88fr)_minmax(0,1.12fr)] lg:gap-10 xl:gap-12">
              <Reveal className="max-w-xl lg:max-w-none">
                <p className="section-label">{LANDING_PAGE_UI.heroEyebrow}</p>
                <h1
                  id="hero-heading"
                  className="mt-4 font-display text-[clamp(2.1rem,4.6vw,3.85rem)] font-semibold tracking-[-0.045em] leading-[1.05] text-ink"
                >
                  {LANDING_PAGE_UI.heroTitle}
                </h1>
                <p className="mt-4 max-w-[42ch] text-base leading-7 text-muted md:mt-6 md:text-lg md:leading-8">
                  {LANDING_PAGE_UI.heroSubtitle}
                </p>
                <div className="mt-7 hidden flex-col gap-3 sm:mt-9 sm:flex-row sm:items-center lg:flex">
                  <ButtonLink variant="primary" href={tenantAppHref(LANDING_PAGE_ROUTE.signup)}>
                    {LANDING_PAGE_UI.heroPrimaryCta}
                    <ArrowRight size={16} strokeWidth={2} />
                  </ButtonLink>
                  <ButtonLink variant="secondary" href={`#${LANDING_PAGE_SECTION.day}`}>
                    {LANDING_PAGE_UI.heroSecondaryCta}
                  </ButtonLink>
                </div>
                <p className="mt-5 hidden text-sm font-medium text-muted lg:mt-6 lg:block">
                  {LANDING_PAGE_UI.heroProof}
                </p>
              </Reveal>

              <Reveal delay={0.1} className="relative min-w-0 lg:justify-self-stretch">
                <div className="hero-desk-frame">
                  <OwnerCommandCenter variant="hero" showFloatCards />
                </div>
              </Reveal>

              <Reveal className="lg:hidden">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <ButtonLink variant="primary" href={tenantAppHref(LANDING_PAGE_ROUTE.signup)}>
                    {LANDING_PAGE_UI.heroPrimaryCta}
                    <ArrowRight size={16} strokeWidth={2} />
                  </ButtonLink>
                  <ButtonLink variant="secondary" href={`#${LANDING_PAGE_SECTION.day}`}>
                    {LANDING_PAGE_UI.heroSecondaryCta}
                  </ButtonLink>
                </div>
                <p className="mt-5 text-sm font-medium text-muted">{LANDING_PAGE_UI.heroProof}</p>
              </Reveal>
            </div>
          </div>
        </section>

        <ProblemStrip />
        <DayTimeline />
        <WorkflowExplorer />

        <section className="section-band-ink" aria-labelledby="mid-cta-heading">
          <div className="page-shell py-14 md:py-16">
            <Reveal className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div className="max-w-xl">
                <h2
                  id="mid-cta-heading"
                  className="font-display text-2xl font-semibold tracking-tight md:text-3xl"
                  style={{ color: 'rgb(var(--desk-ink))' }}
                >
                  {LANDING_PAGE_UI.midCtaTitle}
                </h2>
                <p className="mt-3 text-base leading-7" style={{ color: 'rgb(var(--desk-muted))' }}>
                  {LANDING_PAGE_UI.midCtaSubtitle}
                </p>
              </div>
              <ButtonLink variant="primary" href={tenantAppHref(LANDING_PAGE_ROUTE.signup)}>
                {LANDING_PAGE_UI.midCtaPrimary}
                <ArrowRight size={16} strokeWidth={2} />
              </ButtonLink>
            </Reveal>
          </div>
        </section>

        <CapabilityExplorer />
        <EcosystemMap />
        <FaqAccordion />
        <FinalCta />
      </main>

      <SiteFooter />
    </div>
  )
}
